"use client";
import { useCallback, useEffect, useState } from "react"
import { NodeSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Icons ---
import { ChevronDownIcon } from "@/tiptap/icons/chevron-down-icon"

export const TURN_INTO_BLOCKS = [
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "taskList",
  "blockquote",
  "callout",
  "codeBlock",
]

// Same set as the / menu in an accordion title: paragraph or heading only.
export const ACCORDION_TITLE_TURN_INTO_BLOCKS = ["paragraph", "heading"]

export const blockTypeOptions = [
  {
    type: "paragraph",
    label: "Text",
    isActive: (editor) =>
      editor.isActive("paragraph") &&
      !editor.isActive("heading") &&
      !editor.isActive("bulletList") &&
      !editor.isActive("orderedList") &&
      !editor.isActive("taskList") &&
      !editor.isActive("blockquote") &&
      !editor.isActive("codeBlock"),
  },
  {
    type: "heading",
    label: "Heading 1",
    level: 1,
    isActive: (editor) => editor.isActive("heading", { level: 1 }),
  },
  {
    type: "heading",
    label: "Heading 2",
    level: 2,
    isActive: (editor) => editor.isActive("heading", { level: 2 }),
  },
  {
    type: "heading",
    label: "Heading 3",
    level: 3,
    isActive: (editor) => editor.isActive("heading", { level: 3 }),
  },
  {
    type: "heading",
    label: "Heading 4",
    level: 4,
    isActive: (editor) => editor.isActive("heading", { level: 4 }),
  },
  {
    type: "bulletList",
    label: "Bulleted list",
    isActive: (editor) => editor.isActive("bulletList"),
  },
  {
    type: "orderedList",
    label: "Numbered list",
    isActive: (editor) => editor.isActive("orderedList"),
  },
  {
    type: "taskList",
    label: "To-do list",
    isActive: (editor) => editor.isActive("taskList"),
  },
  {
    type: "blockquote",
    label: "Blockquote",
    isActive: (editor) => editor.isActive("blockquote"),
  },
  {
    type: "callout",
    label: "Callout",
    isActive: (editor) => editor.isActive("callout"),
  },
  {
    type: "codeBlock",
    label: "Code block",
    isActive: (editor) => editor.isActive("codeBlock"),
  },
]

/**
 * Checks if turn into functionality can be used in the current editor state
 */
export function canTurnInto(editor, allowedBlockTypes) {
  if (!editor || !editor.isEditable) return false

  const blockTypes = allowedBlockTypes || TURN_INTO_BLOCKS
  const { selection } = editor.state

  if (selection instanceof NodeSelection) {
    const nodeType = selection.node.type.name
    return blockTypes.includes(nodeType);
  }

  const { $anchor } = selection
  const nodeType = $anchor.parent.type.name
  return blockTypes.includes(nodeType);
}

/**
 * Gets filtered block type options based on available types
 */
export function getFilteredBlockTypeOptions(blockTypes, editor) {
  const types = blockTypes || TURN_INTO_BLOCKS
  const allowed = editor?.isActive("detailsSummary")
    ? types.filter((type) => ACCORDION_TITLE_TURN_INTO_BLOCKS.includes(type))
    : types

  return blockTypeOptions.filter((option) => allowed.includes(option.type))
}

/**
 * Gets the currently active block type from the available options
 */
export function getActiveBlockType(
  editor,
  blockTypes
) {
  if (!editor) return getFilteredBlockTypeOptions(blockTypes)[0];

  const filteredOptions = getFilteredBlockTypeOptions(blockTypes, editor)
  const activeOption = filteredOptions.find((option) => option.isActive(editor))
  return activeOption || filteredOptions[0]
}

/**
 * Determines if the turn into dropdown should be visible
 */
export function shouldShowTurnInto(params) {
  const { editor, hideWhenUnavailable, blockTypes } = params

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!editor.isActive("code")) {
    return canTurnInto(editor, blockTypes);
  }

  return true
}

/**
 * Custom hook that provides turn into dropdown functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MyTurnIntoDropdown() {
 *   const {
 *     isVisible,
 *     canToggle,
 *     activeBlockType,
 *     handleOpenChange,
 *     label,
 *     Icon,
 *   } = useTurnIntoDropdown()
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <DropdownMenu onOpenChange={handleOpenChange}>
 *       // dropdown content
 *     </DropdownMenu>
 *   )
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedTurnIntoDropdown() {
 *   const {
 *     isVisible,
 *     activeBlockType,
 *   } = useTurnIntoDropdown({
 *     editor: myEditor,
 *     blockTypes: ["paragraph", "heading", "bulletList"],
 *     hideWhenUnavailable: true,
 *     onOpenChange: (isOpen) => console.log("Dropdown toggled", isOpen),
 *   })
 *
 *   // component implementation
 * }
 * ```
 */
export function useTurnIntoDropdown(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    blockTypes,
    onOpenChange,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)

  const canToggle = canTurnInto(editor, blockTypes)
  const activeBlockType = getActiveBlockType(editor, blockTypes)

  const handleOpenChange = useCallback((open) => {
    if (!editor || !canToggle) return
    setIsOpen(open)
    onOpenChange?.(open)
  }, [canToggle, editor, onOpenChange])

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowTurnInto({
        editor,
        hideWhenUnavailable,
        blockTypes,
      }))
    }

    handleSelectionUpdate()
    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable, blockTypes])

  return {
    isVisible,
    canToggle,
    isOpen,
    setIsOpen,
    activeBlockType,
    handleOpenChange,
    filteredOptions: getFilteredBlockTypeOptions(blockTypes, editor),
    label: `Turn into (current: ${activeBlockType?.label || "Text"})`,
    Icon: ChevronDownIcon,
  };
}
